import { ArpyLinkDto, ArpyLinkRefDto } from 'src/app/models/dto/arpy/arpyLinkDto';
import { ArpyVehicleFunctionDto } from 'src/app/models/dto/arpy/arpyVehicleFunctionDto';
import { ArpyVehicleSubFunctionDto } from 'src/app/models/dto/arpy/arpyVehicleSubFunctionDto';
import { ArpyRequirementDto } from 'src/app/models/dto/arpy/arpyRequirementDto';

export interface ArpyLinkLookups {
  vehicleFunctions: ArpyVehicleFunctionDto[];
  vehicleSubFunctions: ArpyVehicleSubFunctionDto[];
  requirements: ArpyRequirementDto[];
}

// Strips the picker's rich selection down to what's actually persisted. sourceFileVersion
// (string, for display) becomes fileVersion (number) here - that's the real backend field
// name/type (ArpyLinkInput.FileVersion: Int!). parentUid/grandParentUid are omitted
// entirely (not set to undefined) for a top-level VF - JSON.stringify already drops
// undefined-valued keys, but the live object wouldn't, so an explicit `parentUid:
// undefined` here would compare unequal to its own round-tripped (stringify -> parse)
// baseline in IChangeTrackedComponent's dirty check, permanently stuck "changed."
export function toArpyLinkRefDto(link: ArpyLinkDto): ArpyLinkRefDto {
  const ref: ArpyLinkRefDto = {
    type: link.type,
    uid: link.item.uid,
    fileVersion: Number(link.sourceFileVersion) || 0,
  };
  if (link.parentUid !== undefined) ref.parentUid = link.parentUid;
  if (link.grandParentUid !== undefined) ref.grandParentUid = link.grandParentUid;
  return ref;
}

// GraphQL always returns a nullable field's key, set to null when there's no value -
// unlike toArpyLinkRefDto's own output, which omits parentUid/grandParentUid entirely for
// a top-level VF. Every in-memory consumer (duplicate check, selection check, dirty-tracking
// baseline, and the Preview Changes modal's Object.keys()-based diffing) expects the
// "omitted key" shape, so a link freshly loaded from GraphQL has to be normalized into it
// right as it enters the app, same as this file's other to/from-wire conversions.
export function normalizeArpyLinksFromGraphql(links: ArpyLinkRefDto[]): ArpyLinkRefDto[] {
  return links.map(link => {
    const normalized: ArpyLinkRefDto = { type: link.type, uid: link.uid, fileVersion: link.fileVersion };
    if (link.parentUid != null) normalized.parentUid = link.parentUid;
    if (link.grandParentUid != null) normalized.grandParentUid = link.grandParentUid;
    return normalized;
  });
}

// Reverse of toArpyLinkRefDto: resolves a persisted reference back against the currently-
// loaded Arpy catalog so the UI has a full item to display again. Returns undefined if the
// uid no longer exists in the current "latest" file - a re-uploaded TEESA file renamed or
// dropped it. Callers filter those out. See Arpy-VF-implementation.md open question 6
// (stale link handling) - not resolved yet, so this silently drops rather than surfacing a
// "broken link" state; revisit once that's decided.
export function rehydrateArpyLink(ref: ArpyLinkRefDto, lookups: ArpyLinkLookups): ArpyLinkDto | undefined {
  const item =
    ref.type === 'VehicleFunction' ? lookups.vehicleFunctions.find(vf => vf.uid === ref.uid) :
    ref.type === 'VehicleSubFunction' ? lookups.vehicleSubFunctions.find(sf => sf.uid === ref.uid) :
    lookups.requirements.find(req => req.uid === ref.uid);

  if (!item) return undefined;

  return {
    type: ref.type,
    item,
    parentUid: ref.parentUid,
    grandParentUid: ref.grandParentUid,
    sourceFileVersion: String(ref.fileVersion),
  };
}

export interface ArpyVsfGroupNode {
  uid: string;
  name: string;
  // Present (and removable) only if this VSF is itself directly selected. Undefined
  // means this node exists purely for wayfinding - a requirement under it is selected,
  // but the VSF itself never got checked - the UI should render it muted, with no
  // remove icon (there's nothing to remove).
  link?: ArpyLinkDto;
  requirements: ArpyLinkDto[];
}

export interface ArpyVfGroupNode {
  uid: string;
  name: string;
  // Same "undefined = wayfinding only" meaning as ArpyVsfGroupNode.link.
  link?: ArpyLinkDto;
  vsfGroups: ArpyVsfGroupNode[];
  // Requirements attached straight to this VF, with no VSF in between.
  directRequirements: ArpyLinkDto[];
}

// Groups a flat selection by REAL ancestry - each requirement/VSF's own
// vehicleFunctionUid/vehicleSubFunctionUid - rather than by "is the ancestor also
// selected". That second approach (this function's predecessor) silently reparented a
// requirement onto its grandparent VF whenever its real, immediate VSF parent wasn't
// itself checked, which then let it get grouped in with an unrelated sibling VSF that
// happened to render first. Real ancestry is stable regardless of what's checked, so a
// VF/VSF with selected descendants but no direct selection of its own still gets a node
// here (link: undefined) purely so its real name can be shown - the UI mutes it and
// hides its remove icon rather than pretending it doesn't exist or blaming a sibling.
export function buildArpyAncestryTree(
  links: ArpyLinkDto[],
  vfNameByUid: Map<string, string>,
  vsfByUid: Map<string, ArpyVehicleSubFunctionDto>
): ArpyVfGroupNode[] {
  const vfNodes = new Map<string, ArpyVfGroupNode>();
  const vfOrder: string[] = [];
  const vsfNodes = new Map<string, ArpyVsfGroupNode>();

  const vfNode = (uid: string): ArpyVfGroupNode => {
    let node = vfNodes.get(uid);
    if (!node) {
      node = { uid, name: vfNameByUid.get(uid) ?? uid, vsfGroups: [], directRequirements: [] };
      vfNodes.set(uid, node);
      vfOrder.push(uid);
    }
    return node;
  };

  const vsfNode = (uid: string, vfUid: string): ArpyVsfGroupNode => {
    let node = vsfNodes.get(uid);
    if (!node) {
      node = { uid, name: vsfByUid.get(uid)?.name ?? uid, requirements: [] };
      vsfNodes.set(uid, node);
      vfNode(vfUid).vsfGroups.push(node);
    }
    return node;
  };

  for (const link of links) {
    if (link.type === 'VehicleFunction') {
      const vf = link.item as ArpyVehicleFunctionDto;
      vfNode(vf.uid).link = link;
    } else if (link.type === 'VehicleSubFunction') {
      const vsf = link.item as ArpyVehicleSubFunctionDto;
      vsfNode(vsf.uid, vsf.vehicleFunctionUid).link = link;
    } else {
      const req = link.item as ArpyRequirementDto;
      if (req.vehicleSubFunctionUid) {
        const vfUid = req.vehicleFunctionUid ?? vsfByUid.get(req.vehicleSubFunctionUid)?.vehicleFunctionUid;
        if (vfUid) vsfNode(req.vehicleSubFunctionUid, vfUid).requirements.push(link);
      } else if (req.vehicleFunctionUid) {
        vfNode(req.vehicleFunctionUid).directRequirements.push(link);
      }
    }
  }

  return vfOrder.map(uid => vfNodes.get(uid)!);
}

// Removes exactly one link occurrence (matched on the full type/uid/parentUid/
// grandParentUid tuple, not uid alone - the same VSF/requirement uid can appear under
// more than one VF) and, if it's a VF or VSF, every descendant link scoped to this exact
// occurrence too - a parent can't be removed while leaving some of what's under it still
// linked. Requirements are leaves, so removing one never cascades.
export function removeLinkAndDescendants(
  links: ArpyLinkDto[],
  target: Pick<ArpyLinkDto, 'type' | 'item' | 'parentUid' | 'grandParentUid'>
): ArpyLinkDto[] {
  const uid = target.item.uid;

  return links.filter(link => {
    if (link.type === target.type && link.item.uid === uid &&
        link.parentUid === target.parentUid && link.grandParentUid === target.grandParentUid)
      return false;

    if (target.type === 'VehicleFunction')
      return link.parentUid !== uid && link.grandParentUid !== uid;

    if (target.type === 'VehicleSubFunction')
      return !(link.parentUid === uid && link.grandParentUid === target.parentUid);

    return true;
  });
}

// Removes exactly one link occurrence, then walks back up to its parent (and grandparent)
// removing those too if they're left with no other children - so deleting the only
// requirement under a VSF also drops that now-empty VSF, and if that VSF was the VF's only
// remaining child, drops the VF as well. Stops as soon as an ancestor still has other
// content under it, so a sibling VSF/requirement (and the VF they share) is never touched.
export function removeLinkAndEmptyAncestors(
  links: ArpyLinkDto[],
  target: Pick<ArpyLinkDto, 'type' | 'item' | 'parentUid' | 'grandParentUid'>
): ArpyLinkDto[] {
  let result = removeLinkAndDescendants(links, target);

  // VF children are its VSFs plus any requirement linked straight to it; a VSF's only
  // children are requirements - either way "has children" is just "does anything still
  // in the list point at this uid as its parent".
  const hasChildren = (type: 'VehicleFunction' | 'VehicleSubFunction', uid: string): boolean =>
    type === 'VehicleFunction'
      ? result.some(link => (link.type === 'VehicleSubFunction' || link.type === 'Requirement') && link.parentUid === uid)
      : result.some(link => link.type === 'Requirement' && link.parentUid === uid);

  const removeIfNowEmpty = (type: 'VehicleFunction' | 'VehicleSubFunction', uid: string, parentUid?: string): void => {
    if (hasChildren(type, uid)) return;
    result = result.filter(link => !(link.type === type && link.item.uid === uid && link.parentUid === parentUid));
  };

  if (target.type === 'Requirement') {
    if (target.grandParentUid) {
      removeIfNowEmpty('VehicleSubFunction', target.parentUid!, target.grandParentUid);
      removeIfNowEmpty('VehicleFunction', target.grandParentUid);
    } else if (target.parentUid) {
      removeIfNowEmpty('VehicleFunction', target.parentUid);
    }
  } else if (target.type === 'VehicleSubFunction' && target.parentUid) {
    removeIfNowEmpty('VehicleFunction', target.parentUid);
  }

  return result;
}
